import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Checkbox
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { PilotApis } from 'apis/pilotAPI';
import { ClusterApis } from 'apis/clusterAPI';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId, selectPilotList } from 'store/dataReducers/pilotsSlice';
import { useToast } from '../../context/ToastContext';
import { BglOverlayLoader } from '../../components/loading/BglOverlayLoader';
import { PermissionGate } from 'components/access/PermissionGate';
import { UI_PAGES } from 'framework/access/UiRegistry';

const BUCKET_TO_UPLOAD = 'bidgely-adhoc-productqa/customerDataGenerator';

interface UserMetadata {
  userUUID: string;
  firstName?: string;
  lastName?: string;
  meterType?: string;
  dcxClusters?: string[];
  ratePlan?: string;
  billingCycle?: string;
  primaryEmail?: string;
  secondaryEmail?: string;
  address?: string;
  zipcode?: string;
  personaName?: string;
  personaDescription?: string;
  sourcePilotId?: string;
  sourceUUID?: string;
  bbDuration?: string;
}

export const UpdateUserMetadata = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState('Loading user details...');
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [userMetadata, setUserMetadata] = useState<UserMetadata[]>([]);
  const [utilityName, setUtilityName] = useState<string>('');
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList);
  
  // State for dropdown menus
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeDropdown, setActiveDropdown] = useState<string>('');
  const [activeUserIndex, setActiveUserIndex] = useState<number>(-1);

  // State for clusters
  const [clusters, setClusters] = useState<string[]>([]);
  const [clusterNameToId, setClusterNameToId] = useState<Record<string, string>>({});
  const [clustersLoading, setClustersLoading] = useState(false);

  // State for rate plans
  const [ratePlans, setRatePlans] = useState<string[]>([]);
  const [ratePlansLoading, setRatePlansLoading] = useState(false);

  // State for billing cycle modal
  const [billingCycleModalOpen, setBillingCycleModalOpen] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<string>('');
  const [billingCycleUserIndex, setBillingCycleUserIndex] = useState<number>(-1);
  const [applyBillingCycleToAll, setApplyBillingCycleToAll] = useState(false);

  // State for rate plan modal
  const [ratePlanModalOpen, setRatePlanModalOpen] = useState(false);
  const [selectedRatePlan, setSelectedRatePlan] = useState<string>('');
  const [ratePlanUserIndex, setRatePlanUserIndex] = useState<number>(-1);
  const [applyRatePlanToAll, setApplyRatePlanToAll] = useState(false);

  // State for DCX cluster modal
  const [dcxClusterModalOpen, setDcxClusterModalOpen] = useState(false);
  const [selectedDcxClusters, setSelectedDcxClusters] = useState<string[]>([]);
  const [dcxClusterUserIndex, setDcxClusterUserIndex] = useState<number>(-1);
  const [originalDcxClusters, setOriginalDcxClusters] = useState<string[]>([]);

  // State for unsaved changes confirmation
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<'close' | 'discard' | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [changesSaved, setChangesSaved] = useState(false);

  // Email validation regex
  const emailRegex = /^[a-zA-Z][a-zA-Z0-9._%+-]{1,}@[a-zA-Z0-9]+\.[a-zA-Z]{2,}$/;

  // Email validation function
  const isValidEmail = (email: string): boolean => {
    if (!email || email.trim() === '') return true; 
    return emailRegex.test(email.trim());
  };

  // Name validation regex - letters (including accented), spaces, hyphens, and apostrophes allowed
  const nameRegex = /^[\p{L}\s'-]+$/u;

  // Name validation function
  const isValidName = (name: string): boolean => {
    if (!name || name.trim() === '') return true; 
    return nameRegex.test(name.trim());
  };

  const dcxClustersOptions = clusters; 
  const ratePlanOptions = ratePlans.length > 0 ? ratePlans : [];
  const billingCycleOptions = [
    { value: 'CDG_01', label: '1st of every month', subtext: 'Billing runs from 1st to end of month' },
    { value: 'CDG_05', label: '5th of every month', subtext: 'Billing runs from 5th to 4th of next month' },
    { value: 'CDG_15', label: '15th of every month', subtext: 'Billing runs from 15th to 14th of next month' },
    { value: 'CDG_21', label: '21st of every month', subtext: 'Billing runs from 21st to 20th of next month' }
  ];
  const bbDurationOptions = ['0 Month', '3 Month', '6 Month', '12 Month'];

  const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>, dropdownType: string, userIndex: number) => {
    setAnchorEl(event.currentTarget);
    setActiveDropdown(dropdownType);
    setActiveUserIndex(userIndex);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
    setActiveDropdown('');
    setActiveUserIndex(-1);
  };

  const checkForUnsavedChanges = () => {
    // Check if any user metadata has been modified from original values
    const hasChanges = userMetadata.some((user, index) => {
      // Compare with original values if they exist
      // For now, we'll consider any non-empty values as changes
      return user.firstName || user.lastName || user.meterType || 
             user.dcxClusters?.length || user.ratePlan || user.billingCycle ||
             user.primaryEmail || user.secondaryEmail || user.address || 
             user.zipcode || user.personaName || user.personaDescription ||
             (user.bbDuration && user.bbDuration !== '0 Month');
    });
    setHasUnsavedChanges(hasChanges);
    if (hasChanges) {
      setChangesSaved(false);
    }
  };

  const fetchClusters = async () => {
    try {
      setClustersLoading(true);
      
      const selectedPilot = pilotList.find(pilot => String(pilot.projectId) === String(selectedPilotId));
      
      if (!selectedPilot || !selectedPilot.projectId) {
        console.warn('Selected pilot not found or missing projectId');
        return;
      }
      
      const response = await ClusterApis.getClusters(rc, selectedPilot.projectId);
      
      if (response.success && response.data) {
        // Extract cluster names from the response
        const clusterData = response.data;
        let physicalClusterNames: string[] = [];
        const nameToId: Record<string, string> = {};

        const payload = clusterData?.original_data?.payload;
        if (Array.isArray(payload)) {
          payload.forEach((item: any) => {
            const itemClusters = item?.clusters;
            if (Array.isArray(itemClusters)) {
              itemClusters.forEach((c: any) => {
                const category = String(c?.clusterCategory || '').toLowerCase();
                const clusterReference = c?.clusterReference || '';
                if (category === 'physical' && clusterReference !== 'DEFAULT') {
                  const name = c?.clusterName || c?.name || c?.cluster_name || 'Unknown Cluster';
                  const id = c?.clusterId || c?.id || c?.cluster_id;
                  if (name && id && !nameToId[name]) {
                    nameToId[name] = String(id);
                    physicalClusterNames.push(name);
                  }
                }
              });
            }
          });
        }
        
        if (physicalClusterNames.length === 0 && clusterData.clusters && Array.isArray(clusterData.clusters)) {
          clusterData.clusters.forEach((c: any) => {
            const category = String(c?.clusterCategory || '').toLowerCase();
            const clusterReference = c?.clusterReference || '';
            if (category === 'physical' && clusterReference !== 'DEFAULT') {
              const name = c?.clusterName || c?.name || c?.cluster_name || 'Unknown Cluster';
              const id = c?.clusterId || c?.id || c?.cluster_id;
              if (name && id && !nameToId[name]) {
                nameToId[name] = String(id);
                physicalClusterNames.push(name);
              }
            }
          });
        }

        setClusters(physicalClusterNames);
        setClusterNameToId(nameToId);
      } else {
        console.warn('Failed to fetch clusters:', response.error);
      }
    } catch (error) {
      console.error('Error fetching clusters:', error);
    } finally {
      setClustersLoading(false);
    }
  };

  const fetchRatePlans = async () => {
    try {
      setRatePlansLoading(true);
      
      const selectedPilot = pilotList.find(pilot => String(pilot.projectId) === String(selectedPilotId));
      
      if (!selectedPilot || !selectedPilot.projectId) {
        console.warn('Selected pilot not found or missing projectId for rate plans');
        return;
      }

      const apiHost = (selectedPilot as any).api_host || undefined;
      const utilityId = selectedPilot.projectId?.toString();
      
      if (!utilityId) {
        console.warn('Missing utility_id for rate plans fetch');
        return;
      }
            
      const response = await PilotApis.fetchRatePlans(rc, apiHost, utilityId);
      
      if (response.success && response.data) {
        // Extract rate plan names from the response
        const ratePlanData = response.data;
        let ratePlanNames: string[] = [];
        
        // if (ratePlanData.data && ratePlanData.data.payload && Array.isArray(ratePlanData.data.payload)) {
        //   ratePlanNames = ratePlanData.data.payload.map((plan: any) => {
        //     const planNumber = plan.planNumber || '';
        //     const planName = plan.planName || '';
        //     return `${planNumber}: ${planName}`;
        //   });
        // } else if (ratePlanData.payload && Array.isArray(ratePlanData.payload)) {
        //   ratePlanNames = ratePlanData.payload.map((plan: any) => {
        //     const planNumber = plan.planNumber || '';
        //     const planName = plan.planName || '';
        //     return `${planNumber}: ${planName}`;
        //   });
        // } else if (ratePlanData.rates && Array.isArray(ratePlanData.rates)) {
        //   ratePlanNames = ratePlanData.rates.map((rate: any) => rate.ratePlanName || rate.name || rate.id || 'Unknown Rate Plan');
        // } else if (Array.isArray(ratePlanData)) {
        //   ratePlanNames = ratePlanData.map((rate: any) => rate.ratePlanName || rate.name || rate.id || 'Unknown Rate Plan');
        // }
        if(ratePlanData.data && ratePlanData.data.payload && Array.isArray(ratePlanData.data.payload)) {
          ratePlanNames = ratePlanData.data.payload.map((plan: any) => {
            const planName = plan.planName || 'Unknown Rate Plan';
            const planDescription = plan.planDescription || plan.description || '';
            return planDescription ? `${planName}-${planDescription}` : planName;
          });
        } else if(ratePlanData.payload && Array.isArray(ratePlanData.payload)) {
          ratePlanNames = ratePlanData.payload.map((plan: any) => {
            const planName = plan.planName || 'Unknown Rate Plan';
            const planDescription = plan.planDescription || plan.description || '';
            return planDescription ? `${planName}-${planDescription}` : planName;
          });
        } else if(ratePlanData.data && Array.isArray(ratePlanData.data)) {
          ratePlanNames = ratePlanData.data.map((rate: any) => {
            const planName = rate.ratePlanName || rate.name || rate.id || 'Unknown Rate Plan';
            const planDescription = rate.ratePlanDescription || rate.description || '';
            return planDescription ? `${planName}-${planDescription}` : planName;
          });
        }
        
        setRatePlans(ratePlanNames);
      } else {
        console.warn('Failed to fetch rate plans:', response.error);
      }
    } catch (error) {
      console.error('Error fetching rate plans:', error);
    } finally {
      setRatePlansLoading(false);
    }
  };

  const handleFieldChange = (userIndex: number, field: keyof UserMetadata, value: string) => {
    const safeValue = value || (field === 'bbDuration' ? '0 Month' : '');
    
    const updatedMetadata = [...userMetadata];
    updatedMetadata[userIndex] = {
      ...updatedMetadata[userIndex],
      [field]: safeValue
    };
    setUserMetadata(updatedMetadata);
  };

  const handleDropdownSelect = (value: string) => {
    if (activeUserIndex >= 0 && activeUserIndex < userMetadata.length) {
      const updatedMetadata = [...userMetadata];
      switch (activeDropdown) {
        case 'dcxClusters':
          // Check if cluster is already selected
          const currentClusters = updatedMetadata[activeUserIndex].dcxClusters || [];
          if (!currentClusters.includes(value)) {
            updatedMetadata[activeUserIndex].dcxClusters = [...currentClusters, value];
            rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.METADATA_FIELD_ADDED, { field: 'dcxClusters' })
          }
          break;
        case 'ratePlan':
          updatedMetadata[activeUserIndex].ratePlan = value;
          rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.METADATA_FIELD_MAPPED, { field: 'ratePlan' })
          break;
        case 'billingCycle':
          updatedMetadata[activeUserIndex].billingCycle = value;
          rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.METADATA_FIELD_MAPPED, { field: 'billingCycle' })
          break;
      }
      setUserMetadata(updatedMetadata);
    }
    handleDropdownClose();
  };

  const handleRemoveCluster = (userIndex: number, clusterToRemove: string) => {
    const updatedMetadata = [...userMetadata];
    const currentClusters = updatedMetadata[userIndex].dcxClusters || [];
    updatedMetadata[userIndex].dcxClusters = currentClusters.filter(cluster => cluster !== clusterToRemove);
    setUserMetadata(updatedMetadata);
    rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.METADATA_FIELD_REMOVED, { field: 'dcxClusters' })

    const userName = userMetadata[userIndex].firstName || userMetadata[userIndex].lastName || userMetadata[userIndex].userUUID;
    toast.info(`Removed DCX physical cluster "${clusterToRemove}" from user ${userName}`);
  };

  const handleBillingCycleModalOpen = (userIndex: number) => {
    setBillingCycleUserIndex(userIndex);
    setSelectedBillingCycle('');
    setApplyBillingCycleToAll(false);
    setBillingCycleModalOpen(true);
  };

  const handleBillingCycleModalClose = () => {
    setBillingCycleModalOpen(false);
    setSelectedBillingCycle('');
    setBillingCycleUserIndex(-1);
    setApplyBillingCycleToAll(false);
  };

  const handleBillingCycleAssign = () => {
    if (billingCycleUserIndex >= 0 && selectedBillingCycle) {
      const updatedMetadata = [...userMetadata];
      const billingCycleLabel = billingCycleOptions.find(option => option.value === selectedBillingCycle)?.label || selectedBillingCycle;
      
      if (applyBillingCycleToAll) {
        // Apply to all users
        updatedMetadata.forEach(user => {
          user.billingCycle = selectedBillingCycle;
        });
        toast.success(`Billing cycle "${billingCycleLabel}" assigned to all ${updatedMetadata.length} users`);
      } else {
        // Apply to selected user only
        updatedMetadata[billingCycleUserIndex].billingCycle = selectedBillingCycle;
        const userName = userMetadata[billingCycleUserIndex].firstName || userMetadata[billingCycleUserIndex].lastName || userMetadata[billingCycleUserIndex].userUUID;
        toast.success(`Billing cycle "${billingCycleLabel}" assigned to user ${userName}`);
      }
      
      setUserMetadata(updatedMetadata);
      rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.METADATA_FIELD_MAPPED, { field: 'billingCycle', apply_to_all: applyBillingCycleToAll })
      handleBillingCycleModalClose();
    }
  };

  const handleRatePlanModalOpen = (userIndex: number) => {
    setRatePlanUserIndex(userIndex);
    setSelectedRatePlan('');
    setApplyRatePlanToAll(false);
    setRatePlanModalOpen(true);
  };

  const handleRatePlanModalClose = () => {
    setRatePlanModalOpen(false);
    setSelectedRatePlan('');
    setRatePlanUserIndex(-1);
    setApplyRatePlanToAll(false);
  };

  const handleRatePlanAssign = () => {
    if (ratePlanUserIndex >= 0 && selectedRatePlan) {
      const updatedMetadata = [...userMetadata];
      
      if (applyRatePlanToAll) {
        // Apply to all users
        updatedMetadata.forEach(user => {
          user.ratePlan = selectedRatePlan;
        });
        toast.success(`Rate plan "${selectedRatePlan}" assigned to all ${updatedMetadata.length} users`);
      } else {
        // Apply to selected user only
        updatedMetadata[ratePlanUserIndex].ratePlan = selectedRatePlan;
        const userName = userMetadata[ratePlanUserIndex].firstName || userMetadata[ratePlanUserIndex].lastName || userMetadata[ratePlanUserIndex].userUUID;
        toast.success(`Rate plan "${selectedRatePlan}" assigned to user ${userName}`);
      }
      
      setUserMetadata(updatedMetadata);
      rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.METADATA_FIELD_MAPPED, { field: 'ratePlan', apply_to_all: applyRatePlanToAll })
      handleRatePlanModalClose();
    }
  };

  const handleDcxClusterModalOpen = (userIndex: number) => {
    const currentClusters = userMetadata[userIndex]?.dcxClusters || [];
    setDcxClusterUserIndex(userIndex);
    setSelectedDcxClusters([...currentClusters]);
    setOriginalDcxClusters([...currentClusters]);
    setDcxClusterModalOpen(true);
  };

  const handleDcxClusterModalClose = () => {
    setDcxClusterModalOpen(false);
    setSelectedDcxClusters([]);
    setOriginalDcxClusters([]);
    setDcxClusterUserIndex(-1);
  };

  const handleDcxClusterToggle = (cluster: string) => {
    setSelectedDcxClusters(prev => {
      if (prev.includes(cluster)) {
        return prev.filter(c => c !== cluster);
      } else {
        return [...prev, cluster];
      }
    });
  };

  const handleDcxClusterSave = () => {
    if (dcxClusterUserIndex >= 0) {
      const updatedMetadata = [...userMetadata];
      updatedMetadata[dcxClusterUserIndex].dcxClusters = selectedDcxClusters;
      setUserMetadata(updatedMetadata);
      rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.METADATA_FIELD_MAPPED, { field: 'dcxClusters', cluster_count: selectedDcxClusters.length })
      handleDcxClusterModalClose();
      
      const userName = userMetadata[dcxClusterUserIndex].firstName || userMetadata[dcxClusterUserIndex].lastName || userMetadata[dcxClusterUserIndex].userUUID;
      const clusterCount = selectedDcxClusters.length;
      if (clusterCount === 0) {
        toast.info(`All DCX physical clusters removed from user ${userName}`);
      } else if (clusterCount === 1) {
        toast.success(`DCX physical cluster "${selectedDcxClusters[0]}" assigned to user ${userName}`);
      } else {
        toast.success(`${clusterCount} DCX physical clusters assigned to user ${userName}`);
      }
    }
  };

  const handleDcxClusterCancel = () => {
    const hasChanges = JSON.stringify(selectedDcxClusters) !== JSON.stringify(originalDcxClusters);
    
    if (hasChanges) {
      setPendingAction('close');
      setUnsavedChangesModalOpen(true);
    } else {
      handleDcxClusterModalClose();
    }
  };

  const handleUnsavedChangesAction = (action: 'cancel' | 'discard' | 'save') => {
    setUnsavedChangesModalOpen(false);
    
    switch (action) {
      case 'cancel':
        // Go back to DCX cluster modal with same state
        setPendingAction(null);
        break;
      case 'discard':
        // Close without saving
        handleDcxClusterModalClose();
        setPendingAction(null);
        break;
      case 'save':
        // Save and close
        handleDcxClusterSave();
        setPendingAction(null);
        break;
    }
  };

  const handleSaveChanges = async () => {
    setLoaderMessage('Saving changes...');
    setLoading(true);
    setError(null);

      try {
       // Validate names before saving
       for (const user of userMetadata) {
         if (user.firstName && !isValidName(user.firstName)) {
           toast.error(`First name for user ${user.firstName || user.userUUID} can only contain letters, spaces, hyphens, and apostrophes`);
           setLoading(false);
           return;
         }
         if (user.lastName && !isValidName(user.lastName)) {
           toast.error(`Last name for user ${user.firstName || user.userUUID} can only contain letters, spaces, hyphens, and apostrophes`);
           setLoading(false);
           return;
         }
       }

       // Get environment from localStorage
       const userIngestionDataStr = localStorage.getItem('userIngestionData');
       const userIngestionData = userIngestionDataStr ? JSON.parse(userIngestionDataStr) : null;
       const selectedEnv = userIngestionData?.selectedEnvironment || 'NA';
      
      let saveSuccessCount = 0;
      let saveErrorCount = 0;

      for (const user of userMetadata) {
        try {
          const saveMetadataResponse = await PilotApis.saveUserMetadata(rc, {
            first_name: user.firstName || '',
            last_name: user.lastName || '',
            rate_plan: user.ratePlan || '',
            billing_cycle: user.billingCycle || '',
            primary_email: user.primaryEmail || '',
            secondary_email: user.secondaryEmail || '',
            address: user.address || '',
            meter_type: user.meterType || '',
            dcx_clusters: (user.dcxClusters || [])
              .map((clusterName: string) => {
                const id = clusterNameToId[clusterName];
                return id ? `${clusterName} - ${id}` : clusterName;
              })
              .join(',') || '',
            zipcode: user.zipcode || '',
            persona_name: user.personaName || '',
            persona_description: user.personaDescription || '',
            source_uuid: user.sourceUUID || user.userUUID,
            source_pilot_id: user.sourcePilotId || selectedPilotId,
            destination_pilot_id: selectedPilotId,
            source_env: selectedEnv,
            bb_duration: user.bbDuration || '0 Month'
          });

          if (saveMetadataResponse.success) {
            saveSuccessCount++;
            console.log(`Successfully saved metadata for user ${user.userUUID}`);
            if (saveMetadataResponse.data?.message) {
              toast.success(saveMetadataResponse.data.message);
            }
          } else {
            saveErrorCount++;
            // Extract error message from API response
            let errorMessage = 'Failed to save metadata';
            
            // Check response.data.message first (API error format)
            if (saveMetadataResponse.data?.message) {
              const msg = saveMetadataResponse.data.message;
              if (typeof msg === 'string') {
                errorMessage = msg;
              } else if (msg && typeof msg === 'object' && 'message' in msg) {
                errorMessage = (msg as any).message;
              }
            } 
            // Fallback to response.error
            else if (saveMetadataResponse.error) {
              const err = saveMetadataResponse.error;
              if (typeof err === 'string') {
                errorMessage = err;
              } else if (err && typeof err === 'object' && 'message' in err) {
                errorMessage = (err as any).message;
              }
            }
            
            toast.error(errorMessage);
          }
        } catch (error: any) {
          saveErrorCount++;
          console.error(`Error saving metadata for user ${user.userUUID}:`, error);
          const errorMessage = error instanceof Error 
            ? error.message 
            : (typeof error === 'string' ? error : error?.message || 'Failed to save metadata for user');
          toast.error(errorMessage);
        }
      }

      if (saveErrorCount === 0) {
        console.log(`Successfully saved metadata for all ${saveSuccessCount} users`);
        toast.success(`Successfully saved metadata for all ${saveSuccessCount} users`);
        rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.METADATA_SAVED, { user_count : saveSuccessCount })
        setChangesSaved(true);
        setHasUnsavedChanges(false);
      } else {
        console.warn(`Saved ${saveSuccessCount} users successfully, ${saveErrorCount} failed`);
        toast.warning(`Saved ${saveSuccessCount} users successfully, ${saveErrorCount} failed`);
      }

    } catch (error: any) {
      console.error('Error saving user metadata changes:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : (typeof error === 'string' ? error : error?.message || 'Failed to save user metadata changes');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check if all mandatory fields are filled
  const areMandatoryFieldsFilled = () => {
    return userMetadata.every(user => 
      user.firstName && 
      user.firstName.trim() !== '' && 
      user.firstName !== '-' &&
      isValidName(user.firstName) &&
      (!user.lastName || user.lastName.trim() === '' || isValidName(user.lastName)) &&
      user.primaryEmail && 
      user.primaryEmail.trim() !== '' && 
      user.primaryEmail !== '-' &&
      isValidEmail(user.primaryEmail) &&
      (!user.secondaryEmail || user.secondaryEmail.trim() === '' || isValidEmail(user.secondaryEmail)) &&
      user.ratePlan && 
      user.ratePlan.trim() !== '' && 
      user.ratePlan !== 'not assigned' &&
      user.billingCycle && 
      user.billingCycle.trim() !== '' && 
      user.billingCycle !== 'Not Assigned'
    );
  };

  const isNextButtonEnabled = () => {
    return areMandatoryFieldsFilled() && changesSaved;
  };

  // const getSourceBaseUrl = (env: string | null) => {
  //   const environments: Record<string, string> = {
  //     'Nonprod': 'https://nonprodqaapi-external.bidgely.com',
  //     'NA': 'https://naapi.bidgely.com',
  //     'NA2': 'https://naapi2-external.bidgely.com',
  //     'EU': 'https://euapi.bidgely.com',
  //     'CA': 'https://caapi.bidgely.com',
  //     'UAT': ''
  //   };
  //   return environments[env || ''] || '';
  // };

  const handleBackToAddUsers = () => {
    navigate(`${ROUTE_TREE.PROJECT.CUSTOMER_DATA_GENERATOR.path}?addUser=true`, { replace: false });
  };

  const getUtilityNameFromSelectedPilot = async () => {
    try {
      // Get projects to find the selected pilot
      const projectsResponse = await rc.bffClient.getProjects();
      
      if ('error' in projectsResponse) {
        console.error('Failed to fetch projects:', projectsResponse.error);
        throw new Error('Failed to fetch projects');
      }

      const projects = projectsResponse.data?.data;
      const selectedProject = projects?.find((project: any) => project.projectId === selectedPilotId);
      
      if (!selectedProject) {
        throw new Error('Selected project not found');
      }

      if (!selectedProject.name) {
        throw new Error('Utility name (project.name) not found for selected pilot');
      }

      const utilityName = selectedProject.name;
      console.log('Utility name from project:', utilityName);
      setUtilityName(utilityName);
      return utilityName;
    } catch (error) {
      console.error('Error getting utility name:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to get utility name';
      throw new Error(errorMessage);
    }
  };

  const fetchUserMetadata = async (utilityNameFromPilot: string) => {
    try {
      setLoaderMessage('Loading user details...');
      setLoading(true);
      setError(null);
      setInfoMessage(null);

      // Get user ingestion data from localStorage
      const userIngestionDataStr = localStorage.getItem('userIngestionData');
      if (!userIngestionDataStr) {
        throw new Error('User Not Found in session');
      }

      let userIngestionData;
      try {
        userIngestionData = JSON.parse(userIngestionDataStr);
      } catch (parseError) {
        throw new Error('Invalid user ingestion data format. Please go back and select users and meter types.');
      }

      const dataAge = Date.now() - (userIngestionData.timestamp || 0);
      const maxAge = 8 * 60 * 60 * 1000; // 2 hours
      if (dataAge > maxAge) {
        localStorage.removeItem('userIngestionData');
        throw new Error('User ingestion data has expired. Please go back and select users and meter types.');
      }

      const { stagedUuids: userUUIDs, selectedEnvironment: selectedEnv, userMeterFuelSelections } = userIngestionData;
      
      if (!userUUIDs || userUUIDs.length === 0) {
        throw new Error('No user UUIDs found in stored data');
      }

      // Use userMeterFuelSelections directly as the mapping
      const userMeterFuelMap: Record<string, string[]> = userMeterFuelSelections || {};

      if (!userMeterFuelMap || Object.keys(userMeterFuelMap).length === 0) {
        throw new Error('No meter fuel selections found in stored data');
      }

      const requestData = {
        userMeterFuelMap,
        utilityName: utilityNameFromPilot,
        sourceEnvironment: selectedEnv,
        sourceUsername: "",
        sourcePassword: "",
        houseType: "",
        subPilot: "",
        ingestionBucket: "",
        fileUploadBucket: BUCKET_TO_UPLOAD,
        destinationPilotId: selectedPilotId ? String(selectedPilotId) : undefined
      };

      console.log('Fetching user metadata with request:', requestData);

      const response = await PilotApis.fetchUserDetails(rc, requestData);

      if (response.success) {
      console.log('User metadata response:', response.data);
        const respMessage = (response as any)?.data?.message || (response as any)?.message;
        const existingUsers = (response as any)?.data?.existing_users || [];
        
        // Extract user details from detailedUserData array
        const detailedUserData = response.data?.detailedUserData || [];
        
        const sourceCountry = (response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0)
          ? (response.data.data[0]?.sourceCountry || '')
          : '';
        
        // Filter out existing users from stagedUuids in localStorage
        if (existingUsers && Array.isArray(existingUsers) && existingUsers.length > 0) {
          // Filter out existing users from stagedUuids
          const filteredStagedUuids = userUUIDs.filter((uuid: string) => !existingUsers.includes(uuid));
          
          // Update localStorage with filtered UUIDs and sourceCountry
          if (userIngestionDataStr) {
            try {
              const userIngestionDataForUpdate = JSON.parse(userIngestionDataStr);
              userIngestionDataForUpdate.stagedUuids = filteredStagedUuids;
              
              // Also filter out existing users from userMeterFuelSelections
              if (userIngestionDataForUpdate.userMeterFuelSelections) {
                const filteredSelections: Record<string, string[]> = {};
                filteredStagedUuids.forEach((uuid: string) => {
                  if (userIngestionDataForUpdate.userMeterFuelSelections[uuid]) {
                    filteredSelections[uuid] = userIngestionDataForUpdate.userMeterFuelSelections[uuid];
                  }
                });
                userIngestionDataForUpdate.userMeterFuelSelections = filteredSelections;
              }
              
              if (sourceCountry) {
                userIngestionDataForUpdate.sourceCountry = sourceCountry;
              }
              localStorage.setItem('userIngestionData', JSON.stringify(userIngestionDataForUpdate));
              
              console.log(`Filtered out ${existingUsers.length} existing user(s) from stagedUuids and userMeterFuelSelections. Remaining: ${filteredStagedUuids.length}`);
            } catch (parseError) {
              console.warn('Failed to update userIngestionData with filtered UUIDs:', parseError);
            }
          }
        } else if (userIngestionDataStr && sourceCountry) {
          // Update sourceCountry in localStorage if no existing users
          try {
            const userIngestionData = JSON.parse(userIngestionDataStr);
            userIngestionData.sourceCountry = sourceCountry;
            localStorage.setItem('userIngestionData', JSON.stringify(userIngestionData));
          } catch (parseError) {
            console.warn('Failed to update userIngestionData with sourceCountry:', parseError);
          }
        }
        
        if (existingUsers && Array.isArray(existingUsers) && existingUsers.length > 0) {
          if (respMessage && String(respMessage).toLowerCase().includes('already exist')) {
            setInfoMessage(respMessage);
          } else {
            const existingUsersList = existingUsers.join(', ');
            setInfoMessage(`Note: ${existingUsers.length} user(s) already exist: [${existingUsersList}]`);
          }
        } else if (respMessage && String(respMessage).toLowerCase().includes('user persona already exist')) {
          setInfoMessage(respMessage);
        }
        
        // Map the detailedUserData to our UserMetadata interface
        const userDetails = detailedUserData.map((user: any, index: number) => {
          const uuid = userUUIDs[index] || user.userUUID;
          // Get meter fuels for this specific user from the mapping
          const userMeterFuels = userMeterFuelMap[uuid] || [];
          return {
            userUUID: uuid, 
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            meterType: userMeterFuels.join(', ') || user.meterType || '',
            dcxClusters: user.dcxClusters || [], 
            ratePlan: 'not assigned',
            billingCycle: 'Not Assigned', 
            primaryEmail: user.email || '',
            secondaryEmail: '', 
            address: `${user.address || ''}${user.city ? `, ${user.city}` : ''}${user.state ? `, ${user.state}` : ''}`.trim() || '',
            zipcode: user.zipCode || user.zipcode || '',
            personaName: '', 
            personaDescription: '', 
            sourceUUID: uuid, 
            sourcePilotId: user.source_pilot_id?.toString() || selectedPilotId || '',
            bbDuration: '0 Month' 
          };
        });
        
        console.log('Mapped user details:', userDetails);
        setUserMetadata(userDetails);
        setChangesSaved(false); 

        if (selectedPilotId && pilotList.length > 0) {
          fetchClusters();
          fetchRatePlans();
        }
      } else {
        // Handle specific error cases gracefully
        let errorMessage = 'Failed to fetch user metadata';
        
        if (response.error) {
          const errorText = response.error;
          const parts = errorText.split(';');
          errorMessage = parts[0]?.trim() || errorText;
          
          if (!errorText.includes('Error fetching user details') && !errorText.includes('Gateway is not found')) {
            if (errorText.includes('401') || errorText.includes('Access token expired')) {
              errorMessage = 'Access token has expired. Please try again or contact support if the issue persists.';
            } else if (errorText.includes('403')) {
              errorMessage = 'Access denied. Please check your permissions and try again.';
            } else if (errorText.includes('404')) {
              errorMessage = 'User data not found. Please verify the user UUIDs and try again.';
            } else if (errorText.includes('500')) {
              errorMessage = 'Server error occurred. Please try again later.';
            } else if (errorText.includes('timeout') || errorText.includes('Timeout')) {
              errorMessage = 'Request timed out. Please check your connection and try again.';
            } else if (errorText.includes('network') || errorText.includes('Network')) {
              errorMessage = 'Network error occurred. Please check your connection and try again.';
            }
          }
        } 
        else if (response.data?.errorDetails) {
          const errorDetails = response.data.errorDetails;
          const parts = errorDetails.split(';');
          errorMessage = parts[0]?.trim() || errorDetails;
        } else if (response.data?.message) {
          errorMessage = response.data.message;
        }
        
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('Error fetching user metadata:', error);
      
      // Set a more user-friendly error message
      let displayError = 'Failed to fetch user metadata';
      
      if (error.message) {
        displayError = error.message;
      } else if (error.response?.status === 401) {
        displayError = 'Access token has expired. Please try again or contact support if the issue persists.';
      } else if (error.response?.status === 403) {
        displayError = 'Access denied. Please check your permissions and try again.';
      } else if (error.response?.status === 404) {
        displayError = 'User data not found. Please verify the user UUIDs and try again.';
      } else if (error.response?.status >= 500) {
        displayError = 'Server error occurred. Please try again later.';
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        displayError = 'Request timed out. Please check your connection and try again.';
      } else if (error.message?.includes('Network Error')) {
        displayError = 'Network error occurred. Please check your connection and try again.';
      }
      
      setError(displayError);
      toast.error(displayError);
    } finally {
      setLoading(false);
    }
  };

  const handleStartExtraction = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get utility name from selected pilot
      const utilityNameFromPilot = await getUtilityNameFromSelectedPilot();

      // Get user UUIDs from the fetched metadata
      const userUUIDs = userMetadata.map(user => user.userUUID).filter(Boolean);
      
      if (userUUIDs.length === 0) {
        throw new Error('No user UUIDs available for data extraction');
      }

       // Get environment config from localStorage
       const userIngestionDataStr = localStorage.getItem('userIngestionData');
       const userIngestionData = userIngestionDataStr ? JSON.parse(userIngestionDataStr) : null;
       const selectedEnv = userIngestionData?.selectedEnvironment || '';

       const hasBBUserProgram = userMetadata.some(user => user.bbDuration && user.bbDuration !== '0 Month');
       
       if (userIngestionData) {
         userIngestionData.hasBBUserProgram = hasBBUserProgram;
         localStorage.setItem('userIngestionData', JSON.stringify(userIngestionData));
       }

       console.log('Starting data extraction with utility name:', utilityNameFromPilot);
       console.log('User UUIDs for extraction:', userUUIDs);
       console.log('Has BB User Program:', hasBBUserProgram);

       // Navigate to Data Extraction Status page - APIs will be called there with 5-second delay
       // Note: localStorage data will be cleared in DataExtractionStatus.tsx after it's used
       rc.uiRouter.navigate(ROUTE_TREE.PROJECT.DATA_EXTRACTION_STATUS);
      
    } catch (error: any) {
      console.error('Error during data extraction setup:', error);
      setError(error.message || 'Failed to start data extraction');
      toast.error(error.message || 'Failed to start data extraction');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.METADATA_PANEL_OPENED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Get utility name when component mounts
    getUtilityNameFromSelectedPilot()
      .then(utilityName => {
        if (utilityName) {
          fetchUserMetadata(utilityName);
        }
      })
      .catch(error => {
        console.error('Failed to get utility name:', error);
        setError(error.message || 'Failed to get utility name');
        toast.error(error.message || 'Failed to get utility name');
      });
  }, [selectedPilotId, pilotList]);

  useEffect(() => {
    checkForUnsavedChanges();
  }, [userMetadata]);

  return (
    <Box sx={{ p: 3, position: 'relative', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        mb: 4
      }}>
        <Typography variant="h4" sx={{ fontWeight: 600, textAlign: 'left' }}>
          Update User Metadata
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToAddUsers}
          variant="outlined"
          sx={{
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderColor: theme.palette.primary.main,
            }
          }}
        >
          Back to Add Users
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {infoMessage && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {infoMessage}
        </Alert>
      )}

      {/* User Information Section */}
      <Card sx={{ 
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        boxShadow: 1
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, textAlign: 'left' }}>
              User Information
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'left' }}>
              Review and update metadata for the staged users before starting the data extraction process. Fields marked with * are mandatory.
            </Typography>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ 
            maxHeight: 600, 
            borderRadius: 1, 
            boxShadow: 1, 
            bgcolor: theme.palette.background.paper,
            border: '1px solid',
            borderColor: theme.palette.divider
          }}>
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.background.default }}>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Persona Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Persona Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>First Name *</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Last Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Meter Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>DCX Physical Cluster(s)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Rate Plan *</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Billing Cycle *</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>BB Duration</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Primary Email *</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Secondary Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Address *</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Zipcode</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Source UUID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Source Pilot ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userMetadata.map((user, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={user.personaName || ''}
                        onChange={(e) => handleFieldChange(index, 'personaName', e.target.value)}
                        fullWidth
                        sx={{ minWidth: '120px',
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={user.personaDescription || ''}
                        onChange={(e) => handleFieldChange(index, 'personaDescription', e.target.value)}
                        sx={{ width: '200px',
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={user.firstName || ''}
                        onChange={(e) => handleFieldChange(index, 'firstName', e.target.value)}
                        error={!!(user.firstName && !isValidName(user.firstName))}
                        helperText={user.firstName && !isValidName(user.firstName) ? 'Only letters, spaces, hyphens, and apostrophes allowed' : ''}
                        fullWidth
                        sx={{ 
                          minWidth: '120px',
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={user.lastName || ''}
                        onChange={(e) => handleFieldChange(index, 'lastName', e.target.value)}
                        error={!!(user.lastName && !isValidName(user.lastName))}
                        helperText={user.lastName && !isValidName(user.lastName) ? 'Only letters, spaces, hyphens, and apostrophes allowed' : ''}
                        fullWidth
                        sx={{ 
                          minWidth: '120px',
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.meterType || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.dcxClusters && user.dcxClusters.length > 0 ? (
                            user.dcxClusters.map((cluster, clusterIndex) => (
                              <Box
                                key={clusterIndex}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  px: 1,
                                  py: 0.5,
                                  bgcolor: theme.palette.action.hover, 
                                  color: theme.palette.text.primary,
                                  borderRadius: 5, 
                                  fontSize: '0.75rem',
                                  border: `1px solid ${theme.palette.divider}`,
                                  '&:hover': {
                                    bgcolor: theme.palette.action.selected,
                                  }
                                }}
                              >
                                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                  {cluster}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveCluster(index, cluster)}
                                  sx={{
                                    p: 0,
                                    color: theme.palette.text.secondary,
                                    '&:hover': {
                                      bgcolor: 'rgba(0,0,0,0.1)',
                                      color: theme.palette.text.primary,
                                    }
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>×</Typography>
                                </IconButton>
                              </Box>
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Not Assigned
                            </Typography>
                          )}
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDcxClusterModalOpen(index)}
                          disabled={clustersLoading}
                          sx={{ 
                            p: 0.5,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.main,
                              '& .MuiSvgIcon-root': {
                                color: theme.palette.primary.contrastText,
                              }
                            },
                            '&:disabled': {
                              opacity: 0.5
                            }
                          }}
                        >
                          {clustersLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <AddCircleOutlineOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {user.ratePlan || 'not assigned'}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRatePlanModalOpen(index)}
                          disabled={ratePlansLoading}
                          sx={{ 
                            p: 0.5,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.main,
                              '& .MuiSvgIcon-root': {
                                color: theme.palette.primary.contrastText,
                              }
                            },
                            '&:disabled': {
                              opacity: 0.5
                            }
                          }}
                        >
                          {ratePlansLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <AddCircleOutlineOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {user.billingCycle || 'Not Assigned'}
                        </Typography>
                        <IconButton 
                          // disabled={true}
                          size="small" 
                          onClick={() => handleBillingCycleModalOpen(index)}
                          sx={{ 
                            p: 0.5,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.main,
                              '& .MuiSvgIcon-root': {
                                color: theme.palette.primary.contrastText,
                              }
                            }
                          }}
                        >
                          <AddCircleOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120, width: 120 }}>
                        <Select
                          value={(() => {
                            const currentValue = user.bbDuration || '0 Month';
                            const isValidValue = bbDurationOptions.includes(currentValue);
                            return isValidValue ? currentValue : '0 Month';
                          })()}
                          onChange={(e) => {
                            const newValue = e.target.value || '0 Month';
                            handleFieldChange(index, 'bbDuration', newValue);
                          }}
                          sx={{
                            '& .MuiSelect-select': {
                              py: 0.5,
                              fontSize: '0.875rem',
                              textAlign: 'center',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            }
                          }}
                        >
                          {bbDurationOptions.map((option) => (
                            <MenuItem 
                              key={option}
                              value={option}
                              sx={{
                                textAlign: 'center',
                                justifyContent: 'center'
                              }}
                              title={`${option} - BB True Up Period`}
                            >
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={user.primaryEmail || ''}
                        onChange={(e) => handleFieldChange(index, 'primaryEmail', e.target.value)}
                        fullWidth
                        error={!!(user.primaryEmail && !isValidEmail(user.primaryEmail))}
                        helperText={user.primaryEmail && !isValidEmail(user.primaryEmail) ? 'Invalid email format' : ''}
                        sx={{ 
                          minWidth: '150px',
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={user.secondaryEmail || ''}
                        onChange={(e) => handleFieldChange(index, 'secondaryEmail', e.target.value)}
                        fullWidth
                        error={!!(user.secondaryEmail && !isValidEmail(user.secondaryEmail))}
                        helperText={user.secondaryEmail && !isValidEmail(user.secondaryEmail) ? 'Invalid email format' : ''}
                        sx={{ minWidth: '150px',
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={user.address || ''}
                        onChange={(e) => handleFieldChange(index, 'address', e.target.value)}
                        fullWidth
                        sx={{ 
                          minWidth: '120px',
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={user.zipcode || ''}
                        onChange={(e) => handleFieldChange(index, 'zipcode', e.target.value)}
                        fullWidth
                        sx={{ minWidth: '80px',
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>{user.sourceUUID || '-'}</TableCell>
                    <TableCell>{user.sourcePilotId || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Next Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
            <PermissionGate require={UI_PAGES.UPDATE_USER_METADATA.ctas.update.id} mode="disable">
              <Button
                variant="outlined"
                size="large"
                onClick={handleSaveChanges}
                disabled={loading || userMetadata.length === 0 || !hasUnsavedChanges}
                startIcon={<SaveOutlinedIcon />}
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    borderColor: theme.palette.primary.main,
                  },
                  '&:disabled': {
                    borderColor: theme.palette.action.disabled,
                    color: theme.palette.action.disabled,
                  }
                }}
              >
                Save Changes
              </Button>
            </PermissionGate>
            <Button
              variant="contained"
              size="large"
              onClick={handleStartExtraction}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '&:disabled': {
                  backgroundColor: theme.palette.grey[400],
                  color: theme.palette.grey[600],
                  cursor: 'not-allowed',
                }
              }}
              disabled={loading || !utilityName || userMetadata.length === 0 || !isNextButtonEnabled()}
            >
              Next: Start Extraction
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Billing Cycle Modal */}
      <Dialog 
        open={billingCycleModalOpen} 
        onClose={handleBillingCycleModalClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
          px: 5
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Select Billing Cycle - Selected User
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Billing Cycle Setup
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 5 }}>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedBillingCycle}
              onChange={(e) => setSelectedBillingCycle(e.target.value)}
            >
              {billingCycleOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio sx={{ color: theme.palette.primary.main }} />}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {option.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.subtext}
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    mb: 2,
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    '&.Mui-checked': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: theme.palette.primary.light + '20',
                    }
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: 2,
          px: 3,
          pb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={applyBillingCycleToAll}
                onChange={(e) => setApplyBillingCycleToAll(e.target.checked)}
                sx={{
                  color: theme.palette.primary.main,
                  '&.Mui-checked': {
                    color: theme.palette.primary.main,
                  }
                }}
              />
            }
            label="Apply this selection to all users"
            sx={{ mr: 'auto' }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              onClick={handleBillingCycleModalClose} 
              variant="outlined"
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  borderColor: theme.palette.primary.main,
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBillingCycleAssign} 
              variant="contained"
              disabled={!selectedBillingCycle}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '&:disabled': {
                  backgroundColor: theme.palette.primary.light,
                  color: 'white',
                }
              }}
            >
              Assign
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Rate Plan Modal */}
      <Dialog 
        open={ratePlanModalOpen} 
        onClose={handleRatePlanModalClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
          px: 5
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Select Rate Plan - Selected User
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select New Rate Plan
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 5 }}>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedRatePlan}
              onChange={(e) => setSelectedRatePlan(e.target.value)}
            >
              {ratePlansLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  <Typography>Loading rate plans...</Typography>
                </Box>
              ) : ratePlanOptions.length > 0 ? (
                ratePlanOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio sx={{ color: theme.palette.primary.main }} />}
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {option}
                      </Typography>
                    }
                    sx={{ 
                      mb: 2,
                      p: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      '&.Mui-checked': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.primary.light + '20',
                      }
                    }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No rate plans available
                </Typography>
              )}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: 2,
          px: 4,
          pb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={applyRatePlanToAll}
                onChange={(e) => setApplyRatePlanToAll(e.target.checked)}
                sx={{
                  color: theme.palette.primary.main,
                  '&.Mui-checked': {
                    color: theme.palette.primary.main,
                  }
                }}
              />
            }
            label="Apply this selection to all users"
            sx={{ mr: 'auto' }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              onClick={handleRatePlanModalClose} 
              variant="outlined"
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  borderColor: theme.palette.primary.main,
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRatePlanAssign} 
              variant="contained"
              disabled={!selectedRatePlan}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '&:disabled': {
                  backgroundColor: theme.palette.primary.light,
                  color: 'white',
                }
              }}
            >
              Assign
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* DCX Physical Cluster Modal */}
      <Dialog 
        open={dcxClusterModalOpen} 
        onClose={handleDcxClusterModalClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
          px: 5
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Select DCX Physical Clusters - Selected User
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select DCX Physical Clusters
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 5 }}>
          {clusters.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No Physical cluster found
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {clusters.map((cluster) => (
                <Button
                  key={cluster}
                  variant={selectedDcxClusters.includes(cluster) ? 'contained' : 'outlined'}
                  onClick={() => handleDcxClusterToggle(cluster)}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light,
                    },
                    '&.Mui-contained': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  }}
                >
                  {cluster}
                </Button>
              ))}
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selected Clusters: {selectedDcxClusters.length}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button 
              variant="outlined" 
              onClick={handleDcxClusterCancel}
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  borderColor: theme.palette.primary.main,
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleDcxClusterSave}
              disabled={selectedDcxClusters.length === 0}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '&:disabled': {
                  backgroundColor: theme.palette.primary.light,
                  color: 'white',
                }
              }}
            >
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Modal */}
      <Dialog
        open={unsavedChangesModalOpen}
        onClose={() => handleUnsavedChangesAction('cancel')}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
          px: 5
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Unsaved Changes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You have unsaved changes for this user. What would you like to do?
          </Typography>
        </DialogTitle>
        {/* <DialogContent sx={{ pt: 3, px: 5 }}>
          <Typography variant="body2" color="text.secondary">
            You have unsaved changes to the DCX Clusters. Do you want to save them before closing?
          </Typography>
        </DialogContent> */}
        <DialogActions sx={{ 
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: 2,
          px: 5,
          pb: 3,
          gap: 2
        }}>
          <Button
            variant="outlined"
            onClick={() => handleUnsavedChangesAction('cancel')}
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderColor: theme.palette.primary.main,
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleUnsavedChangesAction('discard')}
            sx={{
              color: theme.palette.error.main,
              borderColor: theme.palette.error.main,
              '&:hover': {
                backgroundColor: theme.palette.error.light,
                color: theme.palette.error.dark,
                borderColor: theme.palette.error.main,
              }
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            onClick={() => handleUnsavedChangesAction('save')}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              '&:disabled': {
                backgroundColor: theme.palette.primary.light,
                color: 'white',
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <BglOverlayLoader
        open={loading}
        message={loaderMessage}
        size={50}
        backgroundColor="rgba(255, 255, 255, 0.4)"
        zIndex={1400}
      />
    </Box>
  );
};


